import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useEditor } from '../../contexts/EditorContext';
import { useExport } from '../../hooks/useExport';
import PreviewModal from './PreviewModal';
import './Toolbar.css';

const Toolbar = () => {
  const { actions, canUndo, canRedo, state } = useEditor();
  const { exportToPNG, exportToJPG, exportToPDF, exportMultiplePagesAsPDF, exportMultiplePagesAsImages } = useExport();
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showMultiPageOptions, setShowMultiPageOptions] = useState(false);
  const [exportFormat, setExportFormat] = useState('pdf');
  const [exportPageOption, setExportPageOption] = useState('current');
  const exportMenuRef = useRef(null);

  const handleZoomIn = useCallback(() => {
    actions.setZoom(Math.min(200, state.zoom + 10));
  }, [actions, state.zoom]);

  const handleZoomOut = useCallback(() => {
    actions.setZoom(Math.max(50, state.zoom - 10));
  }, [actions, state.zoom]);

  const handleFitToPage = useCallback(() => {
    actions.setZoom(100);
  }, [actions]);

  // FIXED: Create properly styled element for export
  const createStyledExportElement = useCallback((content) => {
    const tempDiv = document.createElement('div');
    tempDiv.style.width = '620px';
    tempDiv.style.padding = state.design.padding || '40px';
    tempDiv.style.background = state.design.backgroundColor || 'white';
    tempDiv.style.fontFamily = state.design.fontFamily || 'Inter, sans-serif';
    tempDiv.style.fontSize = `${state.design.fontSize || 14}px`;
    tempDiv.style.lineHeight = state.design.lineHeight || 1.6;
    tempDiv.style.letterSpacing = state.design.letterSpacing || 'normal';
    tempDiv.style.color = state.design.textColor || '#2d3748';
    tempDiv.style.minHeight = '800px';
    tempDiv.style.position = 'absolute';
    tempDiv.style.left = '-9999px';
    tempDiv.style.top = '0';
    tempDiv.innerHTML = content;
    
    return tempDiv;
  }, [state.design]);

  // FIXED: Separate handlers for each export type with proper styling
  const handleQuickExport = useCallback(async (format, pageOption = 'current') => {
    try {
      const editorElement = document.querySelector('#editor');
      if (!editorElement) {
        alert('Editor not found');
        return;
      }

      const allPages = state.pages.map(page => page.content);
      const currentPageIndex = state.currentPageIndex;
      const fileName = `resume-${Date.now()}`;

      switch(format) {
        case 'png':
          if (pageOption === 'current' || state.pages.length === 1) {
            // Create styled clone for current page
            const styledElement = createStyledExportElement(allPages[currentPageIndex]);
            document.body.appendChild(styledElement);
            await exportToPNG(styledElement, `${fileName}.png`);
            document.body.removeChild(styledElement);
          } else if (pageOption === 'all') {
            // Export all pages as images in ZIP
            const styledPages = allPages.map(content => {
              const elem = createStyledExportElement(content);
              document.body.appendChild(elem);
              return elem;
            });
            
            await exportMultiplePagesAsImages(allPages.map((_, i) => {
              const elem = styledPages[i];
              const content = elem.outerHTML;
              document.body.removeChild(elem);
              return content;
            }), 'png', `${fileName}.zip`);
          } else {
            // Specific page
            const pageIndex = parseInt(pageOption);
            if (!isNaN(pageIndex) && pageIndex >= 0 && pageIndex < allPages.length) {
              const styledElement = createStyledExportElement(allPages[pageIndex]);
              document.body.appendChild(styledElement);
              await exportToPNG(styledElement, `${fileName}-page-${pageIndex + 1}.png`);
              document.body.removeChild(styledElement);
            }
          }
          break;

        case 'jpg':
          if (pageOption === 'current' || state.pages.length === 1) {
            const styledElement = createStyledExportElement(allPages[currentPageIndex]);
            document.body.appendChild(styledElement);
            await exportToJPG(styledElement, `${fileName}.jpg`);
            document.body.removeChild(styledElement);
          } else if (pageOption === 'all') {
            await exportMultiplePagesAsImages(allPages, 'jpg', `${fileName}.zip`);
          } else {
            const pageIndex = parseInt(pageOption);
            if (!isNaN(pageIndex) && pageIndex >= 0 && pageIndex < allPages.length) {
              const styledElement = createStyledExportElement(allPages[pageIndex]);
              document.body.appendChild(styledElement);
              await exportToJPG(styledElement, `${fileName}-page-${pageIndex + 1}.jpg`);
              document.body.removeChild(styledElement);
            }
          }
          break;

        case 'pdf':
          if (pageOption === 'current' || state.pages.length === 1) {
            const styledElement = createStyledExportElement(allPages[currentPageIndex]);
            document.body.appendChild(styledElement);
            await exportToPDF(styledElement, `${fileName}.pdf`);
            document.body.removeChild(styledElement);
          } else if (pageOption === 'all') {
            await exportMultiplePagesAsPDF(allPages, `${fileName}.pdf`);
          } else {
            const pageIndex = parseInt(pageOption);
            if (!isNaN(pageIndex) && pageIndex >= 0 && pageIndex < allPages.length) {
              const styledElement = createStyledExportElement(allPages[pageIndex]);
              document.body.appendChild(styledElement);
              await exportToPDF(styledElement, `${fileName}-page-${pageIndex + 1}.pdf`);
              document.body.removeChild(styledElement);
            }
          }
          break;
      }

      setShowExportMenu(false);
      setShowMultiPageOptions(false);
    } catch (error) {
      console.error('Export failed:', error);
      alert(`Export failed: ${error.message}`);
    }
  }, [state.pages, state.currentPageIndex, state.design, createStyledExportElement, exportToPNG, exportToJPG, exportToPDF, exportMultiplePagesAsPDF, exportMultiplePagesAsImages]);

  // FIXED: Multi-page export handler with proper styling
  const handleMultiPageExport = useCallback(async () => {
    try {
      const editorElement = document.querySelector('#editor');
      if (!editorElement) {
        alert('Editor not found');
        return;
      }

      const allPages = state.pages.map(page => page.content);
      const fileName = `resume-${Date.now()}`;

      switch(exportFormat) {
        case 'png':
          if (exportPageOption === 'current' || state.pages.length === 1) {
            const styledElement = createStyledExportElement(allPages[state.currentPageIndex]);
            document.body.appendChild(styledElement);
            await exportToPNG(styledElement, `${fileName}.png`);
            document.body.removeChild(styledElement);
          } else if (exportPageOption === 'all') {
            await exportMultiplePagesAsImages(allPages, 'png', `${fileName}.zip`);
          } else {
            const pageIndex = parseInt(exportPageOption);
            if (!isNaN(pageIndex) && pageIndex >= 0 && pageIndex < allPages.length) {
              const styledElement = createStyledExportElement(allPages[pageIndex]);
              document.body.appendChild(styledElement);
              await exportToPNG(styledElement, `${fileName}-page-${pageIndex + 1}.png`);
              document.body.removeChild(styledElement);
            }
          }
          break;

        case 'jpg':
          if (exportPageOption === 'current' || state.pages.length === 1) {
            const styledElement = createStyledExportElement(allPages[state.currentPageIndex]);
            document.body.appendChild(styledElement);
            await exportToJPG(styledElement, `${fileName}.jpg`);
            document.body.removeChild(styledElement);
          } else if (exportPageOption === 'all') {
            await exportMultiplePagesAsImages(allPages, 'jpg', `${fileName}.zip`);
          } else {
            const pageIndex = parseInt(exportPageOption);
            if (!isNaN(pageIndex) && pageIndex >= 0 && pageIndex < allPages.length) {
              const styledElement = createStyledExportElement(allPages[pageIndex]);
              document.body.appendChild(styledElement);
              await exportToJPG(styledElement, `${fileName}-page-${pageIndex + 1}.jpg`);
              document.body.removeChild(styledElement);
            }
          }
          break;

        case 'pdf':
          if (exportPageOption === 'current' || state.pages.length === 1) {
            const styledElement = createStyledExportElement(allPages[state.currentPageIndex]);
            document.body.appendChild(styledElement);
            await exportToPDF(styledElement, `${fileName}.pdf`);
            document.body.removeChild(styledElement);
          } else if (exportPageOption === 'all') {
            await exportMultiplePagesAsPDF(allPages, `${fileName}.pdf`);
          } else {
            const pageIndex = parseInt(exportPageOption);
            if (!isNaN(pageIndex) && pageIndex >= 0 && pageIndex < allPages.length) {
              const styledElement = createStyledExportElement(allPages[pageIndex]);
              document.body.appendChild(styledElement);
              await exportToPDF(styledElement, `${fileName}-page-${pageIndex + 1}.pdf`);
              document.body.removeChild(styledElement);
            }
          }
          break;
      }

      setShowExportMenu(false);
      setShowMultiPageOptions(false);
    } catch (error) {
      console.error('Export failed:', error);
      alert(`Export failed: ${error.message}`);
    }
  }, [exportFormat, exportPageOption, state.pages, state.currentPageIndex, createStyledExportElement, exportToPNG, exportToJPG, exportToPDF, exportMultiplePagesAsPDF, exportMultiplePagesAsImages]);

  const togglePreview = useCallback(() => {
    setShowPreview(true);
  }, []);

  // Handle clicks outside export menu
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) {
        setShowExportMenu(false);
        setShowMultiPageOptions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl + + for zoom in
      if ((e.ctrlKey || e.metaKey) && (e.key === '+' || e.key === '=')) {
        e.preventDefault();
        handleZoomIn();
      }
      // Ctrl + - for zoom out
      if ((e.ctrlKey || e.metaKey) && e.key === '-') {
        e.preventDefault();
        handleZoomOut();
      }
      // Ctrl + 0 for fit to page
      if ((e.ctrlKey || e.metaKey) && e.key === '0') {
        e.preventDefault();
        handleFitToPage();
      }
      // F11 for preview
      if (e.key === 'F11') {
        e.preventDefault();
        togglePreview();
      }
      // Ctrl + P for print
      if ((e.ctrlKey || e.metaKey) && e.key === 'p') {
        e.preventDefault();
        setShowPreview(true);
      }
      // Escape to close preview
      if (e.key === 'Escape' && showPreview) {
        e.preventDefault();
        setShowPreview(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleZoomIn, handleZoomOut, handleFitToPage, togglePreview, showPreview]);

  // Mouse wheel zoom
  useEffect(() => {
    const handleWheel = (e) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        if (e.deltaY < 0) {
          handleZoomIn();
        } else {
          handleZoomOut();
        }
      }
    };

    window.addEventListener('wheel', handleWheel, { passive: false });
    return () => window.removeEventListener('wheel', handleWheel);
  }, [handleZoomIn, handleZoomOut]);

  // Reset page option when pages change
  useEffect(() => {
    if (state.pages.length === 1) {
      setExportPageOption('current');
    }
  }, [state.pages.length]);

  return (
    <>
      <div className="toolbar">
        <div className="toolbar-left">
          <div className="toolbar-group">
            <button
              className="toolbar-btn"
              onClick={actions.undo}
              disabled={!canUndo}
              title="Undo (Ctrl+Z)"
            >
              <i className="fas fa-undo"></i>
              <span>Undo</span>
            </button>
            <button
              className="toolbar-btn"
              onClick={actions.redo}
              disabled={!canRedo}
              title="Redo (Ctrl+Y)"
            >
              <i className="fas fa-redo"></i>
              <span>Redo</span>
            </button>
          </div>

          <div className="toolbar-group">
            <span className="toolbar-label">View:</span>
            <button
              className="toolbar-btn"
              onClick={handleZoomOut}
              title="Zoom Out (Ctrl+-)"
            >
              <i className="fas fa-search-minus"></i>
            </button>
            <div className="zoom-control">
              <input
                type="range"
                min="50"
                max="200"
                value={state.zoom}
                onChange={(e) => actions.setZoom(parseInt(e.target.value))}
                className="zoom-slider"
              />
              <span className="zoom-value">{state.zoom}%</span>
            </div>
            <button
              className="toolbar-btn"
              onClick={handleZoomIn}
              title="Zoom In (Ctrl++)"
            >
              <i className="fas fa-search-plus"></i>
            </button>
            <button
              className="toolbar-btn"
              onClick={handleFitToPage}
              title="Fit to Page (Ctrl+0)"
            >
              <i className="fas fa-expand-alt"></i>
            </button>
          </div>
        </div>

        <div className="toolbar-center">
          <div className="document-info">
            <span className="page-count">
              Page {state.currentPageIndex + 1} of {state.pages.length}
            </span>
          </div>
        </div>

        <div className="toolbar-right">
          <div className="toolbar-group">
            <button
              className="toolbar-btn"
              onClick={togglePreview}
              title="Preview (F11)"
            >
              <i className="fas fa-eye"></i>
              <span>Preview</span>
            </button>

            <div
              className="export-container"
              ref={exportMenuRef}
            >
              <button 
                className="toolbar-btn primary"
                onClick={() => {
                  if (state.pages.length > 1) {
                    setShowMultiPageOptions(!showMultiPageOptions);
                  }
                  setShowExportMenu(!showExportMenu);
                }}
              >
                <i className="fas fa-download"></i>
                <span>Export</span>
                <i className="fas fa-chevron-down"></i>
              </button>
              
              {showExportMenu && (
                <div className="export-menu">
                  {state.pages.length > 1 && showMultiPageOptions ? (
                    <div className="multi-page-export">
                      <div className="export-section">
                        <h4>Select Format</h4>
                        <div className="format-options">
                          <label className="format-option">
                            <input
                              type="radio"
                              name="exportFormat"
                              value="png"
                              checked={exportFormat === 'png'}
                              onChange={(e) => setExportFormat(e.target.value)}
                            />
                            <i className="fas fa-file-image"></i>
                            <span>PNG</span>
                          </label>
                          <label className="format-option">
                            <input
                              type="radio"
                              name="exportFormat"
                              value="jpg"
                              checked={exportFormat === 'jpg'}
                              onChange={(e) => setExportFormat(e.target.value)}
                            />
                            <i className="fas fa-file-image"></i>
                            <span>JPG</span>
                          </label>
                          <label className="format-option">
                            <input
                              type="radio"
                              name="exportFormat"
                              value="pdf"
                              checked={exportFormat === 'pdf'}
                              onChange={(e) => setExportFormat(e.target.value)}
                            />
                            <i className="fas fa-file-pdf"></i>
                            <span>PDF</span>
                          </label>
                        </div>
                      </div>
                      
                      <div className="export-section">
                        <h4>Select Pages</h4>
                        <div className="page-options">
                          <label className="page-option">
                            <input
                              type="radio"
                              name="exportPage"
                              value="current"
                              checked={exportPageOption === 'current'}
                              onChange={(e) => setExportPageOption(e.target.value)}
                            />
                            <span>Current Page Only</span>
                          </label>
                          <label className="page-option">
                            <input
                              type="radio"
                              name="exportPage"
                              value="all"
                              checked={exportPageOption === 'all'}
                              onChange={(e) => setExportPageOption(e.target.value)}
                            />
                            <span>All Pages</span>
                            <span className="page-count-badge">{state.pages.length}</span>
                          </label>
                          
                          <div className="specific-pages">
                            <h5>Specific Pages:</h5>
                            <div className="page-list">
                              {state.pages.map((page, index) => (
                                <label key={page.id} className="page-option specific">
                                  <input
                                    type="radio"
                                    name="exportPage"
                                    value={index}
                                    checked={exportPageOption === index.toString()}
                                    onChange={(e) => setExportPageOption(e.target.value)}
                                  />
                                  <span>Page {index + 1}</span>
                                  <span className="page-title">{page.title}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="export-actions">
                        <button 
                          className="export-back-btn"
                          onClick={() => setShowMultiPageOptions(false)}
                        >
                          <i className="fas fa-arrow-left"></i>
                          Back
                        </button>
                        <button 
                          className="export-confirm-btn"
                          onClick={handleMultiPageExport}
                        >
                          <i className="fas fa-download"></i>
                          Export Selected
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <button 
                        onClick={() => handleQuickExport('png')} 
                        className="export-option"
                      >
                        <i className="fas fa-file-image"></i>
                        <span>Export as PNG</span>
                      </button>
                      <button 
                        onClick={() => handleQuickExport('jpg')} 
                        className="export-option"
                      >
                        <i className="fas fa-file-image"></i>
                        <span>Export as JPG</span>
                      </button>
                      <button 
                        onClick={() => handleQuickExport('pdf')} 
                        className="export-option"
                      >
                        <i className="fas fa-file-pdf"></i>
                        <span>Export as PDF</span>
                      </button>
                      
                      {state.pages.length > 1 && (
                        <button 
                          onClick={() => setShowMultiPageOptions(true)}
                          className="export-option multi-page"
                        >
                          <i className="fas fa-copy"></i>
                          <span>Multi-page Export</span>
                          <span className="badge">{state.pages.length}</span>
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <PreviewModal 
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
      />
    </>
  );
};

export default Toolbar;